import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import api, { getCereriGDPRAdmin, rezolvaCerereGDPR } from '../services/api';

const Admin: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [utilizatori, setUtilizatori] = useState<any[]>([]);
  const [feedback, setFeedback] = useState<any>(null);
  const [tracker, setTracker] = useState<any>(null);
  const [audit, setAudit] = useState<any[]>([]);
  const [cereriGDPR, setCereriGDPR] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [eroare, setEroare] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/admin/audit').then(r => setAudit(r.data)).catch(() => {});
    getCereriGDPRAdmin().then(r => setCereriGDPR(r.data)).catch(() => {});

    Promise.all([
      api.get('/admin/statistici-detaliate'),
      api.get('/admin/utilizatori'),
      api.get('/admin/feedback'),
      api.get('/admin/tracker')
    ]).then(([statsRes, utilRes, fbRes, trRes]) => {
      setStats(statsRes.data);
      setUtilizatori(utilRes.data);
      setFeedback(fbRes.data);
      setTracker(trRes.data);
    }).catch((err) => {
      if (err.response?.status === 403) {
        setEroare('Nu ai acces la această pagină.');
      } else {
        setEroare('Eroare la încărcare. Reîncarcă pagina.');
      }
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ padding: '2rem', color: '#e0e0f0' }}>Se încarcă...</div>
  );

  if (eroare) return (
    <div style={{ padding: '2rem' }}>
      <p style={{ color: '#d06060' }}>❌ {eroare}</p>
    </div>
  );

  const ratingData = feedback?.feedback
    ? [1,2,3,4,5].map(r => ({
        rating: `${r}⭐`,
        count: feedback.feedback.filter((f: any) => f.rating === r).length
      }))
    : [];

  return (
    <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', fontFamily: 'DM Sans, sans-serif' }}>
      <h2 style={{ color: '#fff', marginBottom: '0.5rem' }}>⚙️ Admin Dashboard</h2>
      <p style={{ color: '#8080a0', marginBottom: '2rem' }}>Vizibil doar pentru tine.</p>

      {/* Stats generale */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {[
            { label: 'Utilizatori', value: stats.total_utilizatori, icon: '👤' },
            { label: 'Conversații', value: stats.total_conversatii, icon: '💬' },
            { label: 'Înregistrări tracker', value: stats.total_inregistrari_tracker, icon: '📊' },
            { label: 'Planuri generate', value: stats.total_planuri, icon: '📋' },
            { label: 'Feedback primit', value: stats.total_feedback, icon: '⭐' },
          ].map((s, i) => (
            <div key={i} style={{
              background: '#1a1a30', borderRadius: '12px', padding: '1rem',
              border: '1px solid #2a2a4a', textAlign: 'center'
            }}>
              <p style={{ fontSize: '1.5rem', margin: '0 0 0.25rem' }}>{s.icon}</p>
              <p style={{ color: '#fff', fontWeight: 600, fontSize: '1.5rem', margin: '0 0 0.25rem' }}>{s.value}</p>
              <p style={{ color: '#6060a0', fontSize: '0.75rem', margin: 0 }}>{s.label}</p>
            </div>
          ))}
          <div
            onClick={() => navigate('/admin/waitlist')}
            style={{
              background: '#1a1a30', borderRadius: '12px', padding: '1rem',
              border: '1px solid #2a2a4a', textAlign: 'center',
              cursor: 'pointer', transition: 'border-color 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = '#4a4ad8')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = '#2a2a4a')}
          >
            <p style={{ fontSize: '1.5rem', margin: '0 0 0.25rem' }}>
              <i className="ti ti-users" />
            </p>
            <p style={{ color: '#fff', fontWeight: 600, fontSize: '1rem', margin: '0 0 0.25rem' }}>Waitlist</p>
            <p style={{ color: '#6060a0', fontSize: '0.75rem', margin: 0 }}>Gestionează cererile de acces</p>
          </div>
          <div
            onClick={() => navigate('/admin/users')}
            style={{
              background: '#1a1a30', borderRadius: '12px', padding: '1rem',
              border: '1px solid #2a2a4a', textAlign: 'center',
              cursor: 'pointer', transition: 'border-color 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = '#4a4ad8')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = '#2a2a4a')}
          >
            <p style={{ fontSize: '1.5rem', margin: '0 0 0.25rem' }}>
              <i className="ti ti-user-check" />
            </p>
            <p style={{ color: '#fff', fontWeight: 600, fontSize: '1rem', margin: '0 0 0.25rem' }}>Utilizatori</p>
            <p style={{ color: '#6060a0', fontSize: '0.75rem', margin: 0 }}>Gestionează conturile active</p>
          </div>
        </div>
      )}

      {/* Feedback */}
      {feedback && (
        <div style={{ background: '#1a1a30', borderRadius: '16px', padding: '1.5rem', border: '1px solid #2a2a4a', marginBottom: '2rem' }}>
          <h3 style={{ color: '#fff', marginBottom: '1rem' }}>⭐ Feedback utilizatori</h3>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ background: '#12121f', borderRadius: '10px', padding: '0.75rem', textAlign: 'center', flex: 1 }}>
              <p style={{ color: '#6060a0', fontSize: '0.7rem', textTransform: 'uppercase', margin: '0 0 0.25rem' }}>Total</p>
              <p style={{ color: '#fff', fontWeight: 600, fontSize: '1.3rem', margin: 0 }}>{feedback.total}</p>
            </div>
            <div style={{ background: '#12121f', borderRadius: '10px', padding: '0.75rem', textAlign: 'center', flex: 1 }}>
              <p style={{ color: '#6060a0', fontSize: '0.7rem', textTransform: 'uppercase', margin: '0 0 0.25rem' }}>Rating mediu</p>
              <p style={{ color: '#d0a040', fontWeight: 600, fontSize: '1.3rem', margin: 0 }}>{feedback.medie}/5</p>
            </div>
          </div>
          {ratingData.length > 0 && (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={ratingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                <XAxis dataKey="rating" stroke="#4a4a6a" tick={{ fill: '#6060a0', fontSize: 11 }} />
                <YAxis stroke="#4a4a6a" tick={{ fill: '#6060a0', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1a1a30', border: '1px solid #2a2a4a', borderRadius: '8px', color: '#e0e0f0' }} />
                <Bar dataKey="count" fill="#4a4ad8" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
          {feedback.feedback?.filter((f: any) => f.comentariu).slice(0, 5).map((f: any, i: number) => (
            <div key={i} style={{ display: 'flex', gap: '1rem', padding: '0.75rem', background: '#12121f', borderRadius: '8px', marginTop: '0.5rem' }}>
              <span style={{ color: f.rating >= 4 ? '#60d090' : f.rating >= 3 ? '#d0a040' : '#d06060', fontWeight: 600 }}>{f.rating}/5</span>
              <div>
                <p style={{ color: '#e0e0f0', margin: '0 0 0.25rem', fontSize: '0.9rem' }}>{f.comentariu}</p>
                <p style={{ color: '#6060a0', margin: 0, fontSize: '0.75rem' }}>{f.created_at}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tracker global */}
      {tracker && (
        <div style={{ background: '#1a1a30', borderRadius: '16px', padding: '1.5rem', border: '1px solid #2a2a4a', marginBottom: '2rem' }}>
          <h3 style={{ color: '#fff', marginBottom: '1rem' }}>📊 Tracker — statistici globale</h3>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            {[
              { label: 'Total înregistrări', value: tracker.total_inregistrari },
              { label: 'Durere medie', value: `${tracker.durere_medie}/10` },
              { label: 'Utilizatori activi', value: tracker.utilizatori_activi }
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, background: '#12121f', borderRadius: '10px', padding: '0.75rem', textAlign: 'center' }}>
                <p style={{ color: '#6060a0', fontSize: '0.7rem', textTransform: 'uppercase', margin: '0 0 0.25rem' }}>{s.label}</p>
                <p style={{ color: '#fff', fontWeight: 600, margin: 0 }}>{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Utilizatori */}
      <div style={{ background: '#1a1a30', borderRadius: '16px', padding: '1.5rem', border: '1px solid #2a2a4a' }}>
        <h3 style={{ color: '#fff', marginBottom: '1rem' }}>👤 Utilizatori înregistrați ({utilizatori.length})</h3>
        {utilizatori.map((u, i) => (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
            gap: '1rem', padding: '0.75rem', background: '#12121f',
            borderRadius: '8px', marginBottom: '0.5rem'
          }}>
            <span style={{ color: '#fff', fontWeight: 600 }}>{u.nume}</span>
            <span style={{ color: '#a0a0c0', fontSize: '0.85rem' }}>{u.problema}</span>
            <span style={{ color: '#6060a0', fontSize: '0.85rem' }}>{u.durata}</span>
            <span style={{ color: '#4a4a6a', fontSize: '0.8rem' }}>{u.created_at}</span>
          </div>
        ))}
      </div>

      {/* Audit log */}
      <div style={{ background: '#1a1a30', borderRadius: '16px', padding: '1.5rem', border: '1px solid #2a2a4a', marginBottom: '2rem' }}>
        <h3 style={{ color: '#fff', marginBottom: '1rem' }}>🔍 Audit log (ultimele 100 acțiuni)</h3>
        {audit.length === 0 ? (
          <p style={{ color: '#4a4a6a', fontSize: '0.85rem' }}>Nu există înregistrări încă. Apar după primul login sau acces la date utilizatori.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  {['Data', 'Email', 'Acțiune', 'Detalii'].map(h => (
                    <th key={h} style={{ color: '#6060a0', fontWeight: 600, textAlign: 'left', padding: '0.4rem 0.75rem', borderBottom: '1px solid #2a2a4a' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {audit.map((a, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #1a1a2a' }}>
                    <td style={{ color: '#4a4a6a', padding: '0.4rem 0.75rem', whiteSpace: 'nowrap' }}>{a.created_at}</td>
                    <td style={{ color: '#a0a0c0', padding: '0.4rem 0.75rem' }}>{a.email}</td>
                    <td style={{ padding: '0.4rem 0.75rem' }}>
                      <span style={{
                        background: a.actiune.startsWith('admin') ? '#2a1a4a' : a.actiune.startsWith('gdpr') ? '#4a1a1a' : '#1a2a1a',
                        color: a.actiune.startsWith('admin') ? '#a070f0' : a.actiune.startsWith('gdpr') ? '#f07070' : '#70c070',
                        borderRadius: '4px', padding: '0.15rem 0.5rem', fontSize: '0.75rem'
                      }}>{a.actiune}</span>
                    </td>
                    <td style={{ color: '#6060a0', padding: '0.4rem 0.75rem' }}>{a.detalii || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cereri GDPR */}
      <div style={{ background: '#1a1a30', borderRadius: '16px', padding: '1.5rem', border: '1px solid #2a2a4a', marginBottom: '2rem' }}>
        <h3 style={{ color: '#fff', marginBottom: '1rem' }}>
          📋 Cereri GDPR ({cereriGDPR.filter(c => c.status !== 'rezolvat').length} în așteptare)
        </h3>
        {cereriGDPR.length === 0 ? (
          <p style={{ color: '#4a4a6a', fontSize: '0.85rem' }}>Nicio cerere primită.</p>
        ) : cereriGDPR.map((c, i) => (
          <div key={i} style={{
            background: '#12121f', borderRadius: '8px', padding: '0.75rem',
            marginBottom: '0.5rem', border: c.depasit_termen ? '1px solid #4a1a1a' : '1px solid transparent'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ color: '#e0e0f0', fontWeight: 600, fontSize: '0.85rem' }}>{c.tip_cerere}</span>
                  {c.depasit_termen && <span style={{ color: '#d06060', fontSize: '0.72rem', background: '#3a0a0a', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>DEPĂȘIT</span>}
                </div>
                <p style={{ color: '#a0a0c0', fontSize: '0.8rem', margin: '0 0 0.2rem' }}>{c.email}</p>
                {c.descriere && <p style={{ color: '#6060a0', fontSize: '0.8rem', margin: '0 0 0.2rem' }}>{c.descriere}</p>}
                <p style={{ color: '#4a4a6a', fontSize: '0.75rem', margin: 0 }}>
                  {c.created_at} · Termen: {c.deadline_at}
                  {c.rezolvat_la && ` · Rezolvat: ${c.rezolvat_la}`}
                </p>
              </div>
              {c.status !== 'rezolvat' && (
                <button onClick={() => {
                  rezolvaCerereGDPR(c.id).then(() => getCereriGDPRAdmin().then(r => setCereriGDPR(r.data)));
                }} style={{
                  padding: '0.4rem 0.9rem', borderRadius: '6px', border: 'none',
                  background: '#1a3a1a', color: '#60d090', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap'
                }}>
                  Marchează rezolvat
                </button>
              )}
              {c.status === 'rezolvat' && (
                <span style={{ color: '#60d090', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>✓ Rezolvat</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Reset popup test */}
      <div style={{ marginTop: '2rem', background: '#1a1a30', borderRadius: '16px', padding: '1.5rem', border: '1px solid #2a2a4a' }}>
        <h3 style={{ color: '#fff', marginBottom: '1rem' }}>🔧 Test tools</h3>
        <button onClick={() => {
          localStorage.removeItem('popup_shown');
          alert('Resetat! Reloghează-te.');
        }} style={{
          padding: '0.6rem 1.2rem', borderRadius: '8px', border: '1px solid #2a2a4a',
          background: '#12121f', color: '#a0a0c0', cursor: 'pointer'
        }}>🔄 Reseteaza popup</button>
      </div>
    </div>
  );
};

export default Admin;
